import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";

const CartCtx = createContext(null);
const limit = (p) => Math.min(p.stock, p.max_per_order || 5);
const EDITION = ["language", "market_region", "set_name", "set_code", "category", "contents", "pack_count", "condition", "sealed", "sku", "availability_status", "preorder"];
const snap = (p) => ({ id: p.id, slug: p.slug, name_de: p.name_de, name_en: p.name_en, price: p.price, image: p.images?.[0],
  stock: p.stock, max_per_order: p.max_per_order, ...Object.fromEntries(EDITION.map((k) => [k, p[k]])) });
const NOTICE = {
  unavailable: ["Dieses Produkt ist nicht mehr verfügbar und wurde entfernt.", "This product is no longer available and was removed."],
  out_of_stock: ["Ausverkauft – wurde aus dem Warenkorb entfernt.", "Sold out – removed from your cart."],
  qty_reduced: ["Nur noch {n} verfügbar – Menge wurde angepasst.", "Only {n} available – quantity adjusted."],
  price_changed: ["Preis geändert: vorher {old}, jetzt {new}.", "Price changed: was {old}, now {new}."],
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => JSON.parse(localStorage.getItem("pn28_cart") || "[]"));
  const [open, setOpen] = useState(false);

  useEffect(() => { localStorage.setItem("pn28_cart", JSON.stringify(items)); }, [items]);

  const add = useCallback((p, qty = 1) => {
    setItems((prev) => {
      const ex = prev.find((i) => i.id === p.id);
      if (ex) return prev.map((i) => (i.id === p.id ? { ...i, quantity: Math.min(i.quantity + qty, limit(p)) } : i));
      return [...prev, { ...snap(p), quantity: Math.min(qty, limit(p)) }];
    });
    setOpen(true);
  }, []);
  const setQty = useCallback((id, q) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity: Math.max(1, Math.min(q, limit(i))) } : i))), []);
  const remove = useCallback((id) => setItems((prev) => prev.filter((i) => i.id !== id)), []);
  const clear = useCallback(() => setItems([]), []);
  const [notices, setNotices] = useState({});
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const validate = useCallback(async () => {
    const cur = itemsRef.current;
    if (!cur.length) return { ok: true, removed: [] };
    const { data } = await api.post("/cart/validate", { items: cur.map((i) => ({ id: i.id, quantity: i.quantity, price: i.price })) });
    const de = (localStorage.getItem("pn28_lang") || "de") === "de";
    const fmt = (v) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(v);
    const next = [], msgs = {}, removed = [];
    data.lines.forEach((l) => {
      const old = cur.find((i) => i.id === l.id);
      const fill = (k) => NOTICE[k][de ? 0 : 1].replace("{n}", l.quantity).replace("{old}", fmt(l.old_price)).replace("{new}", fmt(l.product?.price || 0));
      if (l.issue) msgs[l.id] = fill(l.issue) + (l.price_changed && l.issue !== "price_changed" ? ` ${fill("price_changed")}` : "");
      if (!l.product || l.quantity <= 0) { removed.push({ ...old, notice: msgs[l.id] }); return; }
      next.push({ ...snap(l.product), quantity: l.quantity });
    });
    setItems(next); setNotices(msgs);
    return { ok: Object.keys(msgs).length === 0, removed };
  }, []);

  const value = useMemo(() => ({
    items, add, setQty, remove, clear, open, setOpen, validate, notices,
    count: items.reduce((s, i) => s + i.quantity, 0),
    subtotal: items.reduce((s, i) => s + i.price * i.quantity, 0),
  }), [items, open, add, setQty, remove, clear, validate, notices]);

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
};

export const useCart = () => useContext(CartCtx);
